// PWA Installation Utilities

let deferredPrompt = null;

// Check if app is installable
export const isInstallable = () => {
  return deferredPrompt !== null;
};

// Check if app is already installed
export const isAppInstalled = () => {
  // Check if running in standalone mode (installed PWA)
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  // Check for iOS standalone mode
  if (window.navigator.standalone === true) {
    return true;
  }
  return false;
};

// Store the install prompt event
export const captureInstallPrompt = (event) => {
  event.preventDefault();
  deferredPrompt = event;
  return true;
};

// Trigger the install prompt
export const promptInstall = async () => {
  if (!deferredPrompt) {
    console.log('Install prompt not available');
    return { outcome: 'unavailable' };
  }

  deferredPrompt.prompt();
  const result = await deferredPrompt.userChoice;
  deferredPrompt = null;
  return result;
};

// Register service worker.
//
// Behaviour update (May 2026 — paired with service-worker.js v3):
// - Force an update check on every page load so a redeploy doesn't sit
//   behind a stale SW for hours.
// - When a new SW finishes installing and is in "waiting", tell it to
//   activate immediately (`skipWaiting`) and reload the page once it
//   takes control. This auto-recovers users whose v2 SW was serving
//   stale chunk references → blank pages on production after redeploys.
let _reloadingForNewSW = false;
export const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const registration = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/',
      updateViaCache: 'none',
    });

    // Force an update check on every page load. Cheap network call; the
    // browser short-circuits if the SW byte-equals the registered copy.
    try { registration.update(); } catch (_) { /* no-op */ }

    const activateWaiting = (worker) => {
      if (!worker) return;
      try { worker.postMessage({ type: 'SKIP_WAITING' }); } catch (_) { /* no-op */ }
    };

    // If a new SW is already waiting at registration time, push it live.
    if (registration.waiting) activateWaiting(registration.waiting);

    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (!newWorker) return;
      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          activateWaiting(newWorker);
        }
      });
    });

    // When the new SW takes control, reload once so the user gets a
    // clean page served fully from network.
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (_reloadingForNewSW) return;
      _reloadingForNewSW = true;
      window.location.reload();
    });

    return registration;
  } catch (error) {
    console.error('PWA: Service Worker registration failed', error);
    return null;
  }
};

// Unregister service worker (for debugging)
export const unregisterServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const registration of registrations) {
      await registration.unregister();
    }
    console.log('PWA: All service workers unregistered');
  }
};
